import { Ticket } from '../../types/ticket.types';
import { ManageTicketDisplayInputRow } from './manageTicketGrouping';

export type ManageTicketSortColumn = 'event' | 'section' | 'row' | 'price' | 'lastPriced';
export type ManageTicketSortDirection = 'asc' | 'desc';

export type ManageTicketTableSort = {
  column: ManageTicketSortColumn;
  direction: ManageTicketSortDirection;
};

export type ManageTicketSortValues = {
  eventDate: string | number | null;
  section: string | null;
  row: string | null;
  price: string | number | null;
  lastPriced: string | null;
};

const SORTABLE_MANAGE_TICKET_TABS = new Set(['Verification', 'Listed', 'Sold']);

export const isManageTicketTableSortEnabled = (
  tab: string,
  isMailParserErrorView = false,
): boolean => !isMailParserErrorView && SORTABLE_MANAGE_TICKET_TABS.has(tab);

export const isManageTicketSortRequestPending = (
  pendingKey: string | null,
  contextKey: string,
): boolean => pendingKey !== null && pendingKey === contextKey;

export const shouldShowManageTicketQueryLoader = (
  loading: boolean,
  hasData: boolean,
  sortRequestPending: boolean,
): boolean => loading && !hasData && !sortRequestPending;

const getRowTicket = (row: Exclude<ManageTicketDisplayInputRow, { kind: 'section' }>): Ticket =>
  row.kind === 'group' ? row.tickets[0] : row.ticket;

const isNumericValue = (value: string): boolean =>
  value.trim() !== '' && Number.isFinite(Number(value));

const compareValues = (
  left: string | number | null,
  right: string | number | null,
  column: ManageTicketSortColumn,
  direction: ManageTicketSortDirection,
): number => {
  if (left == null) return right == null ? 0 : 1;
  if (right == null) return -1;

  let comparison: number;
  if (typeof left === 'number' && typeof right === 'number') {
    comparison = left - right;
  } else {
    const leftText = String(left);
    const rightText = String(right);
    if (
      (column === 'section' || column === 'row') &&
      isNumericValue(leftText) &&
      isNumericValue(rightText)
    ) {
      comparison = Number(leftText) - Number(rightText);
    } else {
      comparison = leftText.localeCompare(rightText, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
  }

  return direction === 'asc' ? comparison : -comparison;
};

export const sortManageTicketRowsByValues = <T,>(
  rows: T[],
  sort: ManageTicketTableSort | null,
  getValues: (row: T) => ManageTicketSortValues,
): T[] => {
  if (!sort) return rows;
  return rows
    .map((row, index) => ({ row, index, values: getValues(row) }))
    .sort((left, right) =>
      compareValues(
        getSortValueFromValues(left.values, sort.column),
        getSortValueFromValues(right.values, sort.column),
        sort.column,
        sort.direction,
      ) || left.index - right.index,
    )
    .map(({ row }) => row);
};

const getSortValueFromValues = (
  values: ManageTicketSortValues,
  column: ManageTicketSortColumn,
): string | number | null => {
  switch (column) {
    case 'event': {
      if (values.eventDate == null || values.eventDate === '') return null;
      if (typeof values.eventDate === 'number') return values.eventDate;
      const timestamp = Date.parse(values.eventDate);
      return Number.isFinite(timestamp) ? timestamp : null;
    }
    case 'section':
      return values.section == null ? null : values.section.trim();
    case 'row':
      return values.row == null ? null : values.row.trim();
    case 'price': {
      if (values.price == null || String(values.price).trim() === '') return null;
      const price = Number(values.price);
      return Number.isFinite(price) ? price : null;
    }
    case 'lastPriced': {
      if (!values.lastPriced) return null;
      const timestamp = Date.parse(values.lastPriced);
      return Number.isFinite(timestamp) ? timestamp : null;
    }
  }
};

const sortRows = (
  rows: Exclude<ManageTicketDisplayInputRow, { kind: 'section' }>[],
  sort: ManageTicketTableSort,
): Exclude<ManageTicketDisplayInputRow, { kind: 'section' }>[] =>
  sortManageTicketRowsByValues(rows, sort, (row) => {
    const ticket = getRowTicket(row);
    return {
      eventDate: ticket.e_date,
      section: ticket.tp_section,
      row: ticket.tp_row,
      price: ticket.tp_list_price,
      lastPriced: ticket.last_priced_at ?? null,
    };
  });

export const sortManageTicketDisplayRows = (
  rows: ManageTicketDisplayInputRow[],
  sort: ManageTicketTableSort | null,
): ManageTicketDisplayInputRow[] => {
  if (!sort) return rows;

  const sortedRows: ManageTicketDisplayInputRow[] = [];
  let pendingRows: Exclude<ManageTicketDisplayInputRow, { kind: 'section' }>[] = [];
  const flushPendingRows = () => {
    sortedRows.push(...sortRows(pendingRows, sort));
    pendingRows = [];
  };

  rows.forEach((row) => {
    if (row.kind === 'section') {
      flushPendingRows();
      sortedRows.push(row);
      return;
    }
    pendingRows.push(row);
  });
  flushPendingRows();

  return sortedRows;
};
